import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useMutation, useQuery } from 'convex/react';
import { api } from '../lib/api';

export function MagicLinkLogin() {
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const loginWithMagicLink = useMutation(api.auth.loginWithMagicLink);

  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [success, setSuccess] = useState(false);

  // Validate the magic link token
  const tokenValidation = useQuery(api.auth.validateMagicLink,
    token && !success ? { token } : 'skip'
  );

  useEffect(() => {
    if (token && tokenValidation?.valid) {
      // Auto-login with the magic link
      handleMagicLinkLogin();
    } else if (tokenValidation && !tokenValidation.valid && !success) {
      setError(tokenValidation.error || 'Enlace inválido o expirado');
      setIsLoading(false);
    }
  }, [token, tokenValidation]);

  const handleMagicLinkLogin = async () => {
    if (!token) {
      setError('Token no válido');
      setIsLoading(false);
      return;
    }

    try {
      const result = await loginWithMagicLink({ token });
      setSuccess(true);

      // Store user data in localStorage
      localStorage.setItem('playerId', result.playerId);
      localStorage.setItem('playerName', result.name);
      localStorage.setItem('playerRole', result.role);

      // Navigate based on role
      setTimeout(() => {
        if (result.role === 'organizer') {
          navigate('/dashboard/organizer');
        } else if (result.role === 'administrator') {
          navigate('/dashboard/admin');
        } else {
          navigate('/dashboard/player');
        }
      }, 1500);
    } catch (err: any) {
      setError(err.message || 'Error al iniciar sesión');
      setIsLoading(false);
    }
  };

  // Show loading while validating token
  if (isLoading && !error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full space-y-8 text-center">
          <img
            src="/torneopadel.png"
            alt="TorneoPadel"
            className="w-32 h-32 mx-auto mb-4 object-contain"
          />
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="text-gray-600">Validando enlace de acceso...</p>
        </div>
      </div>
    );
  }

  // Show success message
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <img
              src="/torneopadel.png"
              alt="TorneoPadel"
              className="w-32 h-32 mx-auto mb-4 object-contain"
            />
            <div className="bg-green-50 p-6 rounded-lg">
              <h2 className="text-2xl font-bold text-green-800 mb-2">
                ¡Inicio de sesión exitoso! ✅
              </h2>
              <p className="text-green-700">
                Redirigiendo al panel...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <img
            src="/torneopadel.png"
            alt="TorneoPadel"
            className="w-24 h-24 mx-auto mb-4 object-contain"
          />
          <h2 className="text-2xl font-bold text-red-600 mb-4">
            Enlace inválido o expirado
          </h2>
          <p className="text-gray-600 mb-6">
            {error === 'El enlace ha expirado'
              ? 'Este enlace ha expirado. Por favor, solicita uno nuevo.'
              : error === 'Este enlace ya ha sido utilizado'
              ? 'Este enlace ya ha sido utilizado.'
              : 'Este enlace no es válido.'}
          </p>
          <div className="space-y-3">
            <Link
              to="/login"
              className="block w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-center"
            >
              Ir a iniciar sesión
            </Link>
            <Link
              to="/"
              className="block w-full px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-center"
            >
              Volver al inicio
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}